import { useSelector } from 'react-redux';
import { selectUser, selectLoginStateWaiting } from 'gossamer-front-end';
import { CustomInfo } from 'cb-unified';
import classes from './Header.module.css';
import { DesktopNav } from './DesktopNav';
import { HeaderDropdown, HeaderDropdownItem } from './HeaderDropdown';
import { HeaderSearchBar } from './HeaderSearchBar';
import { MyInstitutions } from './MyInstitutions';
import { AccountMenu } from './AccountMenu';
import { ActiveTaskBanner, NowEventBanner } from './Banners';
import { useSocketMessaging } from '../hooks/useSocketMessaging';
import { useMyActiveTasks } from '../hooks/useMyActiveTasks';

const HOME_PATH = '/';
const LOGO_SRC = '/assets/icons/careerbook-logo.svg';
const BRAND_NAME = 'Careerbook';
const LOGIN_PATH = '/auth/login';
const REGISTER_PATH = '/auth/user-registration';

const solutions: HeaderDropdownItem[] = [
  {
    title: 'For NHS hospitals and Trusts',
    subtitle: 'Automate UG and PG teaching, attendance, feedback and reporting.',
    href: '/website/hospitals-and-trusts',
    icon: <img src="/assets/icons/for-nhs-hospitals-and-trusts.svg" alt="" />,
  },
  {
    title: 'For universities and student societies',
    subtitle: 'Run teaching series, webinars and conferences — with sponsorship available.',
    href: '/website/universities-and-societies',
    icon: <img src="/assets/icons/for-universities-and-student-societies.svg" alt="" />,
  },
  {
    title: 'For course and CPD providers',
    subtitle: 'List, sell and deliver courses to an audience of 175,000+.',
    href: '/website/course-and-cpd-providers',
    icon: <img src="/assets/icons/for-course-and-cpd-providers.svg" alt="" />,
  },
];

const platform: HeaderDropdownItem[] = [
  ['AI timetabling', 'Build timetables and flag clashes.', 'ai-timetabling'],
  ['Smart attendance', 'Capture trusted attendance records.', 'smart-attendance'],
  ['Feedback and certificates', 'Automate follow-up and evidence.', 'feedback-and-certificates'],
  ['Reminders and alerts', 'Keep learners and admins informed.', 'reminders-and-alerts'],
  ['Reporting and insights', 'Produce audit-ready evidence.', 'reporting-and-insights'],
  ['Workflows and inductions', 'Track every onboarding task.', 'workflows-and-inductions'],
  ['Learner portfolio', 'Keep calendars and evidence together.', 'learner-portfolio'],
  ['Careerbook Live', 'Host and sell live online teaching.', 'careerbook-live'],
].map(([title, subtitle, slug]) => ({
  title,
  subtitle,
  href: `/website/features/${slug}`,
  icon: <img src={`/assets/icons/${slug}.svg`} alt="" />,
}));

interface HeaderProps {}

const LoginOrRegister = () => {
  return (
    <div className={classes.linkGroup}>
      <a href={LOGIN_PATH} className={classes.loginLink}>
        Login
      </a>
      <a href={REGISTER_PATH} className={classes.registerLink}>
        Register
      </a>
    </div>
  );
};

export const Header = (props: HeaderProps) => {
  const user = useSelector(selectUser);
  const isLoginStateWaiting = useSelector(selectLoginStateWaiting);
  const { unread } = useSocketMessaging();
  const { activeTasks } = useMyActiveTasks();
  const isInAdminArea = window.location.pathname.startsWith('/admin');
  const hasActiveTask = activeTasks.length > 0;
  const adminInstitutionId = (user?.customInfo as CustomInfo)?.adminInstitutionId || null;
  const followingInstitutionIds = (user?.customInfo as CustomInfo)?.followingInstitutionIds || [];

  return (
    <header className={classes.header}>
      <div className={classes.inner}>
        <div className={classes.desktopRow}>
          <DesktopNav homeHref={HOME_PATH} logoSrc={LOGO_SRC} brandName={BRAND_NAME} />
          <HeaderDropdown label="Solutions" items={solutions} />
          <HeaderDropdown label="Platform" items={platform} />
          <HeaderSearchBar />
          {user && !isInAdminArea && (
            <MyInstitutions adminInstitutionId={adminInstitutionId} followingInstitutionIds={followingInstitutionIds} />
          )}
          {isInAdminArea && <div style={{ width: 150 }}>&nbsp;</div>}
          {isLoginStateWaiting ? (
            <div>Loading...</div>
          ) : user ? (
            <AccountMenu unread={unread} hasActiveTask={hasActiveTask} user={user} />
          ) : (
            <LoginOrRegister />
          )}
        </div>

        <div className={classes.mobileStack}>
          <div className={classes.mobileTopRow}>
            <a href={HOME_PATH} className={classes.brand}>
              <img src={LOGO_SRC} alt="" className={classes.brandIcon} />
            </a>
            {user && !isInAdminArea && (
              <MyInstitutions
                adminInstitutionId={adminInstitutionId}
                followingInstitutionIds={followingInstitutionIds}
              />
            )}
            {user ? <AccountMenu unread={unread} hasActiveTask={hasActiveTask} user={user} /> : <LoginOrRegister />}
          </div>
          <HeaderSearchBar />
        </div>
      </div>

      <NowEventBanner userId={user?.userId || null} />
      {activeTasks.length > 0 && <ActiveTaskBanner nextActiveTask={activeTasks[0]} />}
    </header>
  );
};
